/// PropertyField: Generic property field component that renders appropriate
/// input based on property type (text, textarea, number, select, checkbox, expression, json).

import { useId } from "react";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PropertyFieldProps {
  /** Label displayed above the input. */
  label: string;
  /** Input type determines which control to render. */
  type: "text" | "textarea" | "number" | "select" | "checkbox" | "expression" | "json";
  /** Current value. */
  value: unknown;
  /** Called when value changes. */
  onChange: (value: unknown) => void;
  /** Options for select type. */
  options?: { value: string; label: string }[];
  /** Placeholder text for text/textarea/expression inputs. */
  placeholder?: string;
  /** Whether the field is read-only. */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Shared input class
// ---------------------------------------------------------------------------

const INPUT_BASE =
  "w-full rounded bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm text-zinc-200 " +
  "placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-blue-500 " +
  "disabled:text-zinc-500 disabled:cursor-not-allowed";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PropertyField({
  label,
  type,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}: PropertyFieldProps) {
  const fieldId = useId();

  return (
    <div className="flex flex-col gap-1">
      {type !== "checkbox" && (
        <label htmlFor={fieldId} className="text-xs font-medium text-zinc-400">
          {label}
        </label>
      )}

      {type === "text" && (
        <input
          id={fieldId}
          type="text"
          className={INPUT_BASE}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}

      {type === "number" && (
        <input
          id={fieldId}
          type="number"
          className={INPUT_BASE}
          value={value != null ? Number(value) : ""}
          onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}

      {type === "textarea" && (
        <textarea
          id={fieldId}
          className={`${INPUT_BASE} min-h-[60px] resize-y`}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={3}
        />
      )}

      {type === "expression" && (
        <textarea
          id={fieldId}
          className={`${INPUT_BASE} min-h-[40px] resize-y font-mono text-xs`}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? "Enter expression..."}
          disabled={disabled}
          rows={2}
        />
      )}

      {type === "json" && (
        <textarea
          id={fieldId}
          className={`${INPUT_BASE} min-h-[60px] resize-y font-mono text-xs`}
          value={typeof value === "string" ? value : JSON.stringify(value ?? {}, null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              // Keep raw string until valid JSON
              onChange(e.target.value);
            }
          }}
          placeholder={placeholder ?? "{}"}
          disabled={disabled}
          rows={4}
        />
      )}

      {type === "select" && (
        <select
          id={fieldId}
          className={INPUT_BASE}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">-- Select --</option>
          {options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {type === "checkbox" && (
        <label htmlFor={fieldId} className="flex items-center gap-2 cursor-pointer">
          <input
            id={fieldId}
            type="checkbox"
            className="h-4 w-4 rounded border-zinc-600 bg-zinc-900 text-blue-500 focus:ring-blue-500 focus:ring-offset-zinc-800"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
          <span className="text-xs text-zinc-300">{label}</span>
        </label>
      )}
    </div>
  );
}
